import * as THREE from 'three';
import { CollabMarkupData, MarkupType } from './fiko-collab-types';

/**
 * FikoCollabMarkupManager - Manages 3D markup annotations
 *
 * Supports:
 * - Arrows
 * - Lines
 * - Text labels
 */
export class FikoCollabMarkupManager {
    private container: THREE.Group;
    private markups: Map<string, THREE.Object3D> = new Map();
    private markupData: Map<string, CollabMarkupData> = new Map();

    // Pre-allocated buffers for zero-allocation operations
    private _tmpVec3A: THREE.Vector3 = new THREE.Vector3();
    private _tmpVec3B: THREE.Vector3 = new THREE.Vector3();
    private _tmpVec3C: THREE.Vector3 = new THREE.Vector3();
    private _tmpColor: THREE.Color = new THREE.Color();

    constructor(container: THREE.Group) {
        this.container = container;
    }

    /**
     * Add a markup from data
     */
    addMarkup(data: CollabMarkupData): THREE.Object3D | null {
        if (this.markups.has(data.markupId)) {
            // Already exists, update instead
            return this.updateMarkup(data.markupId, data);
        }

        let markup: THREE.Object3D | null = null;

        switch (data.type) {
            case 'arrow':
                markup = this.createArrow(data);
                break;
            case 'line':
            case 'polyline':
                markup = this.createLine(data);
                break;
            case 'text':
            case 'callout':
                markup = this.createText(data);
                break;
            default:
                console.warn('[CollabMarkup] Unknown markup type:', data.type);
                return null;
        }

        if (markup) {
            markup.name = `Markup_${data.markupId}`;
            markup.userData.collabMarkupId = data.markupId;
            markup.userData.collabUserId = data.userId;
            this.container.add(markup);
            this.markups.set(data.markupId, markup);
            this.markupData.set(data.markupId, data);
        }

        return markup;
    }

    /**
     * Update an existing markup
     */
    updateMarkup(markupId: string, data: Partial<CollabMarkupData>): THREE.Object3D | null {
        const existing = this.markups.get(markupId);
        const existingData = this.markupData.get(markupId);

        if (!existing || !existingData) {
            return null;
        }

        // Remove old and recreate with new data
        this.container.remove(existing);
        this.disposeMarkup(existing);

        const newData: CollabMarkupData = { ...existingData, ...data };
        this.markups.delete(markupId);
        this.markupData.delete(markupId);

        return this.addMarkup(newData);
    }

    /**
     * Delete a markup
     */
    deleteMarkup(markupId: string): boolean {
        const markup = this.markups.get(markupId);
        if (!markup) {
            return false;
        }

        this.container.remove(markup);
        this.disposeMarkup(markup);
        this.markups.delete(markupId);
        this.markupData.delete(markupId);

        return true;
    }

    /**
     * Create arrow markup
     */
    private createArrow(data: CollabMarkupData): THREE.Group {
        const group = new THREE.Group();

        if (data.points.length < 2) {
            return group;
        }

        // Zero-allocation: use pre-allocated buffers
        this._tmpVec3A.set(...data.points[0]); // start
        this._tmpVec3B.set(...data.points[1]); // end
        this._tmpVec3C.subVectors(this._tmpVec3B, this._tmpVec3A); // direction
        const length = this._tmpVec3C.length();

        // Arrow helper
        this._tmpColor.set(data.userColor || '#FF0000');
        const headLength = Math.min(length * 0.2, data.arrowSize || 0.1);
        const headWidth = headLength * 0.5;

        const arrow = new THREE.ArrowHelper(
            this._tmpVec3C.normalize(),
            this._tmpVec3A,
            length,
            this._tmpColor,
            headLength,
            headWidth
        );

        group.add(arrow);

        // Make line thicker using LineBasicMaterial
        const lineMaterial = arrow.line.material as THREE.LineBasicMaterial;
        lineMaterial.linewidth = data.lineWidth || 2;
        lineMaterial.depthTest = false;
        lineMaterial.depthWrite = false;

        // Arrow head
        const coneMaterial = arrow.cone.material as THREE.MeshBasicMaterial;
        coneMaterial.depthTest = false;
        coneMaterial.depthWrite = false;

        group.renderOrder = 8000;

        return group;
    }

    /**
     * Create line/polyline markup
     */
    private createLine(data: CollabMarkupData): THREE.Line {
        // Zero-allocation: create Vector3 array once, reuse for geometry
        const points: THREE.Vector3[] = [];
        for (let i = 0; i < data.points.length; i++) {
            points.push(new THREE.Vector3(...data.points[i]));
        }

        const geometry = new THREE.BufferGeometry().setFromPoints(points);

        // Zero-allocation: use pre-allocated color buffer
        this._tmpColor.set(data.userColor || '#00FF00');
        const material = new THREE.LineBasicMaterial({
            color: this._tmpColor,
            linewidth: data.lineWidth || 2,
            depthTest: false,
            depthWrite: false,
        });

        const line = new THREE.Line(geometry, material);
        line.renderOrder = 8000;

        return line;
    }

    /**
     * Create text/callout markup
     */
    private createText(data: CollabMarkupData): THREE.Sprite {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;

        const text = data.text || '';
        const fontSize = data.fontSize || 16;
        const padding = 10;

        // Measure text
        ctx.font = `bold ${fontSize}px Arial`;
        const metrics = ctx.measureText(text);
        const textWidth = metrics.width;
        const textHeight = fontSize;

        canvas.width = textWidth + padding * 2;
        canvas.height = textHeight + padding * 2;

        // Draw background
        ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
        ctx.roundRect(0, 0, canvas.width, canvas.height, 4);
        ctx.fill();

        // Draw border
        ctx.strokeStyle = data.userColor || '#FFFFFF';
        ctx.lineWidth = 2;
        ctx.stroke();

        // Draw text
        ctx.fillStyle = '#FFFFFF';
        ctx.font = `bold ${fontSize}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(text, canvas.width / 2, canvas.height / 2);

        const texture = new THREE.CanvasTexture(canvas);
        texture.needsUpdate = true;

        const material = new THREE.SpriteMaterial({
            map: texture,
            transparent: true,
            depthTest: false,
            depthWrite: false,
        });

        const sprite = new THREE.Sprite(material);

        // Position at first point
        if (data.points.length > 0) {
            sprite.position.set(...data.points[0]);
        }

        // Scale based on canvas size
        const scaleFactor = 0.01;
        sprite.scale.set(canvas.width * scaleFactor, canvas.height * scaleFactor, 1);
        sprite.renderOrder = 8001;

        return sprite;
    }

    /**
     * Get all markups
     */
    getAllMarkups(): CollabMarkupData[] {
        return Array.from(this.markupData.values());
    }

    /**
     * Get markup by ID
     */
    getMarkup(markupId: string): CollabMarkupData | undefined {
        return this.markupData.get(markupId);
    }

    /**
     * Get markup 3D object
     */
    getMarkupObject(markupId: string): THREE.Object3D | undefined {
        return this.markups.get(markupId);
    }

    /**
     * Get markups by user
     */
    getUserMarkups(userId: string): CollabMarkupData[] {
        return Array.from(this.markupData.values()).filter(m => m.userId === userId);
    }

    /**
     * Clear all markups
     */
    clearAll(): void {
        for (const markup of this.markups.values()) {
            this.container.remove(markup);
            this.disposeMarkup(markup);
        }
        this.markups.clear();
        this.markupData.clear();
    }

    /**
     * Clear markups by user
     */
    clearUserMarkups(userId: string): void {
        for (const [markupId, data] of this.markupData) {
            if (data.userId === userId) {
                this.deleteMarkup(markupId);
            }
        }
    }

    /**
     * Dispose a markup object
     */
    private disposeMarkup(obj: THREE.Object3D): void {
        obj.traverse((child) => {
            if (child instanceof THREE.Mesh) {
                child.geometry?.dispose();
                if (Array.isArray(child.material)) {
                    child.material.forEach(m => m.dispose());
                } else {
                    child.material?.dispose();
                }
            } else if (child instanceof THREE.Line) {
                child.geometry?.dispose();
                (child.material as THREE.Material)?.dispose();
            } else if (child instanceof THREE.Sprite) {
                child.material.map?.dispose();
                child.material.dispose();
            }
        });
    }

    /**
     * Dispose the manager
     */
    dispose(): void {
        this.clearAll();
    }
}
